import { useState } from 'react'
import { Check, X } from 'lucide-react'
import {
  getRetellApiErrorMessage,
  useCreateRetellPhone,
  type CreateRetellPhoneArgs,
} from '../../api/admin-retell-phones'

interface AddRetellPhoneModalProps {
  orgId: string
  orgName: string
  onClose: () => void
}

/**
 * "Add Retell phone" modal — invoked from the org's Retell inventory card.
 *
 * Fields:
 *   * ``e164`` (required) — the public phone number. Validated client-side
 *     for the ``+<digits>`` shape (looser than the backend's full E.164
 *     check) so the operator gets immediate feedback for typos like a
 *     missing ``+``. The backend re-validates and 400s anything malformed,
 *     which we surface as an inline error.
 *   * ``termination_uri`` (optional) — Twilio SIP URI used to import the
 *     number into Retell via BYOT. Mentioned inline because operators
 *     who haven't done this before won't know to fill it.
 *   * ``label`` (optional) — human-readable name for the inventory list.
 *
 * Mirrors the visual primitives in :file:`AdminBillingPhonesTab.tsx`
 * (fixed-overlay modal, ``border-border bg-surface``, ``Check``/``X``
 * action icons) so operators don't have to context-switch between the
 * two providers' provisioning UIs.
 */
export function AddRetellPhoneModal({
  orgId,
  orgName,
  onClose,
}: AddRetellPhoneModalProps) {
  const [e164, setE164] = useState('')
  const [terminationUri, setTerminationUri] = useState('')
  const [label, setLabel] = useState('')
  const create = useCreateRetellPhone(orgId)

  // Lightweight client-side check: anything starting with ``+`` followed by
  // 8+ digits passes. The server has the canonical validator; this just
  // catches the most common operator mistake (missing the ``+``).
  const e164LooksOk = /^\+\d{8,15}$/.test(e164.trim())
  const errMessage = getRetellApiErrorMessage(create.error)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    const args: CreateRetellPhoneArgs = {
      e164: e164.trim(),
      termination_uri: terminationUri.trim() || null,
      label: label.trim() || null,
    }
    try {
      await create.mutateAsync(args)
      onClose()
    } catch {
      // Surface via ``create.error`` below; no rethrow needed.
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md overflow-hidden rounded-lg border border-border bg-surface shadow-xl">
        <header className="flex items-center justify-between border-b border-border px-4 py-3">
          <div className="font-medium">Add Retell phone to {orgName}</div>
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-border p-1 hover:bg-surface-elevated"
            aria-label="Close"
          >
            <X className="h-3 w-3" />
          </button>
        </header>
        <form onSubmit={submit} className="space-y-3 p-4">
          <label className="block space-y-1 text-sm">
            <span className="text-text-secondary">
              E.164 phone number <span className="text-error">*</span>
            </span>
            <input
              type="text"
              value={e164}
              onChange={(ev) => setE164(ev.target.value)}
              placeholder="+13055551234"
              required
              autoFocus
              className="w-full rounded border border-border bg-background px-2 py-1 font-mono text-sm"
            />
            {e164.length > 0 && !e164LooksOk && (
              <div className="text-xs text-warning">
                Expected format: <code>+</code> followed by 8–15 digits (e.g.
                <code> +13055551234</code>).
              </div>
            )}
          </label>

          <label className="block space-y-1 text-sm">
            <span className="text-text-secondary">Termination URI</span>
            <input
              type="text"
              value={terminationUri}
              onChange={(ev) => setTerminationUri(ev.target.value)}
              placeholder="yourtrunk.pstn.twilio.com"
              className="w-full rounded border border-border bg-background px-2 py-1 font-mono text-sm"
            />
            <div className="text-xs text-text-secondary">
              Twilio SIP URI used to import this number into Retell, e.g.{' '}
              <code>yourtrunk.pstn.twilio.com</code>. Leave blank if importing
              via a Retell-managed number.
            </div>
          </label>

          <label className="block space-y-1 text-sm">
            <span className="text-text-secondary">Label</span>
            <input
              type="text"
              value={label}
              onChange={(ev) => setLabel(ev.target.value)}
              placeholder="Primary Voysi line"
              className="w-full rounded border border-border bg-background px-2 py-1 text-sm"
            />
            <div className="text-xs text-text-secondary">
              Optional human-readable name shown in the inventory list.
            </div>
          </label>

          {create.isError && errMessage && (
            <div className="rounded border border-error/30 bg-error/5 p-2 text-xs text-error">
              {errMessage}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded border border-border bg-background px-3 py-1.5 text-sm hover:bg-surface-elevated"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!e164LooksOk || create.isPending}
              className="inline-flex items-center gap-1 rounded bg-primary px-3 py-1.5 text-sm text-white hover:opacity-90 disabled:opacity-50"
            >
              <Check className="h-3.5 w-3.5" />
              {create.isPending ? 'Adding…' : 'Add phone'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
